<template>
  <section>
    <div v-if="isVisible" class="Modal" @click.self="closeModal">
      <div class="ModalContainer">
        <header class="ModalHeader">
          <span class="ModalHeader--Heading">{{ section.theme_settings.productTitle }}</span>
          <div class="ModalHeader--Close" @click="closeModal">
            {{ section.translations.close }}
            <div class="CloseIcon" v-html="section.icons.close"></div>
          </div>
        </header>
        <div class="ModalContent">
          <div
            class="ModalContent--Column Filters--Column"
            :class="{ open: _mobileSectionOpen === 'filters', close: _mobileSectionOpen === 'content' }"
          >
            <template v-if="menu">
              <div class="AccordionContainer">
                <Accordion
                  v-for="item in menu.items"
                  :item="item"
                  :icons="section.icons"
                  :key="item.title"
                  :initialIsOpen="selectedMenuItem && selectedMenuItem.id === item.id"
                  :selectedCategory="selectedCategory"
                  @update:isOpen="onUpdateIsOpen(item)"
                  @onSelectCategory="onSelectCategory($event)"
                ></Accordion>
              </div>
              <div class="ModalSearch desktop-hidden">
                <input
                  id="mobile-search"
                  type="text"
                  placeholder="Search"
                  v-model="_searchInput"
                  v-on:keyup="onSearchKeyUp($event)"
                  @click="onSearchSelect()"
                />
                <div
                  v-if="_searchInput"
                  class="CloseSearchIcon"
                  v-html="section.icons.close"
                  @click="onResetSearch()"
                ></div>
                <div v-else class="SearchIcon" v-html="section.icons.search"></div>
              </div>
            </template>
          </div>
          <div
            class="ModalContent--Column Content--Column"
            :class="{ open: _mobileSectionOpen === 'content', close: _mobileSectionOpen === 'filters' }"
          >
            <div
              v-if="
                selectedMenuItem && (!_searchInput || _searchInput.length === 0) && _mobileSectionOpen === 'content'
              "
              class="CategoryAccordion desktop-hidden"
              @click="_mobileSectionOpen = 'filters'"
            >
              <div class="Title">
                <div>{{ selectedMenuItem.title }}</div>
                <div>
                  <div class="ArrowIcon" v-html="section.icons.arrowBottom"></div>
                </div>
              </div>
            </div>
            <div
              v-if="searchDebounceInput && searchDebounceInput.length > 0 && _mobileSectionOpen === 'content'"
              class="CategoryAccordion desktop-hidden"
              @click="onOpenSearch()"
            >
              <div class="Search">
                <div>{{ searchDebounceInput }}</div>
                <div>
                  <div class="ArrowIcon" v-html="section.icons.arrowBottom"></div>
                </div>
              </div>
            </div>
            <Spinner v-if="_loading" :isOpen="_loading"></Spinner>
            <template v-else>
              <div class="ProductItem--Container">
                <Product
                  v-for="product in filteredProducts"
                  :key="product.id"
                  :icons="section.icons"
                  :product="product"
                  :currency="section.theme_settings.currency"
                  :showPrice="canShowPrice()"
                  @onProductAdd="onProductAdd($event)"
                  @onProductDetail="onProductDetail($event)"
                ></Product>
                <div class="ModalContent--Placeholder" v-if="!selectedCategory && !filteredProducts">
                  <div>{{ section.translations.welcome_title }}</div>
                  <div>{{ section.translations.welcome_subtitle }}</div>
                </div>
                <div class="ModalContent--NoResults" v-if="filteredProducts && filteredProducts.length === 0">
                  <div v-if="searchDebounceInput && searchDebounceInput.length > 0">
                    {{ section.translations.no_results }} "{{ searchDebounceInput }}".
                  </div>
                  <div v-else>{{ section.translations.no_results }} "{{ selectedCategory.title }}".</div>
                </div>
              </div>
              <ProductDetail
                :isOpen="_isProductDetailOpen"
                :icons="section.icons"
                :translations="section.translations"
                :notes="notes"
                :product="selectedProduct"
                @onCloseDetail="onCloseDetail()"
                @onProductAdd="onProductAddFromDetail($event)"
              ></ProductDetail>
            </template>
          </div>
          <div class="ModalContent--Column Selection--Column">
            <div class="SelectedProducts--Container">
              <div class="SelectedProducts--Tile" v-for="product in availableProducts" :key="product.id">
                <div class="SelectedProducts--ImageContainer">
                  <img
                    v-if="product.featuredImage"
                    :src="product.featuredImage.url"
                    :alt="product.featuredImage.altText"
                  />
                </div>
                <div v-if="product.id" class="overlay" @click="onProductRemove(product)">
                  <div class="RemoveIcon" v-html="section.icons.remove"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="ModalFooter">
          <div class="ModalFooter--Container">
            <div class="ModalSearch mobile-hidden">
              <input
                type="text"
                :placeholder="section.translations.search"
                v-model="_searchInput"
                v-on:keyup="onSearchKeyUp($event)"
                @click="onSearchSelect()"
              />
              <div
                v-if="_searchInput"
                class="CloseSearchIcon"
                v-html="section.icons.close"
                @click="onResetSearch()"
              ></div>
              <div v-else class="SearchIcon" v-html="section.icons.search"></div>
            </div>

            <div class="ModalFooter--Actions">
              <div class="ModalFooter--Price">
                {{ partialPrice }}
              </div>
              <button
                class="ModalFooter--AddToCart button button--primary"
                :disabled="!canAddToCart() || canAddProduct()"
                @click="onAddToCart"
              >
                {{ section.translations.add_to_cart }}
              </button>
            </div>
          </div>
          <div class="ModalFooter--Error" v-if="!canAddToCart()">
            {{ section.translations.limit_reached.replace('{LIMIT}', getKitLimitForCart()) }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Accordion from './components/Accordion.vue';
import Product from './components/Product.vue';
import ProductDetail from './components/ProductDetail.vue';
import Spinner from './components/Spinner.vue';

export default {
  components: { Accordion, Product, ProductDetail, Spinner },
  props: {
    _searchInput: '',
    _loading: {
      type: Boolean,
      default: false,
    },
    _isProductDetailOpen: {
      type: Boolean,
      default: false,
    },
    _mobileSectionOpen: {},

    section: {},
    menu: {},
    categoryProducts: {},
    searchProducts: {},
    timeout: null,
    searchDebounceInput: '',
    selectedKit: {},
    selectedMenuItem: {},
    selectedCategory: {},
    selectedProduct: {},
  },
  data: function () {
    return {
      vw: Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0),
      selectedProducts: [],
      isVisible: false,
      notes: ['top', 'heart', 'base'],
    };
  },
  computed: {
    productQueryParams() {
      return `
        id
        handle
        title
        description
        productType
        selectedOrFirstAvailableVariant {
          id
          title
          availableForSale
          price {
            amount
            currencyCode
          }
        }
        featuredImage {
          altText
          url
        }
        metafields(identifiers: [
          {namespace:"product", key:"detail_link"},
        ]) {
          key
          value
          reference {
            ... on Product {
              id
              title
              description
              featuredImage {
                altText
                url
              }
              metafields(identifiers: [
                ${this.notes.map((n) => `{namespace:"product", key:"olfactoryNotes_${n}"}`).join(', ')}
              ]) {
                key
                value
              }
            }
          }
        }
      `;
    },
    /* Other metafields (may be needed in the future)
      {namespace:"product", key:"collection"},
      {namespace:"product", key:"editorial_Title"},
      {namespace:"product", key:"editorial_Description"},
      {namespace:"product", key:"editorialImage_Single"},
      {namespace:"product", key:"editorialImage_Double"},
      {namespace:"product", key:"editorialImage_Double2"},
      {namespace:"product", key:"olfactive_Family"},
      {namespace:"product", key:"olfactoryNotes_image"},
     */
    filteredProducts() {
      if (this.searchDebounceInput && this.searchProducts) {
        return this.searchProducts;
      }
      if (this.selectedCategory) {
        return this.categoryProducts;
      }
      return undefined;
    },
    partialPrice() {
      if (this.selectedKit === 'sample')
        return `${this.section.theme_settings.sample_price || this.section.settings.sample_price || '25'} ${this.section.theme_settings.currency}`;
      if (this.selectedKit === 'discovery') {
        let price = 0;
        for (let product of this.selectedProducts) {
          price += parseFloat(product.selectedOrFirstAvailableVariant.price.amount);
        }
        return `${price} ${this.section.theme_settings.currency}`;
      }
    },
    availableProducts() {
      const placeholderObj = {
        title: 'Placeholder',
        featuredImage: {
          url:
            this.selectedKit === 'sample'
              ? this.section.theme_settings.samplePlaceholderUrl
              : this.section.theme_settings.discoveryPlaceholderUrl,
          altText: 'Placeholder',
        },
      };
      switch (this.selectedKit) {
        case 'sample':
          return this.selectedProducts.concat(Array(4 - this.selectedProducts.length).fill(placeholderObj));
        case 'discovery':
          return this.selectedProducts.concat(Array(3 - this.selectedProducts.length).fill(placeholderObj));
      }
      return [];
    },
  },
  name: 'CustomizationSection',
  created() {
    this.getData();
  },
  mounted() {
    console.log('test vue mounted rest', this);
    window.addEventListener('resize', this.updateVw);
    window.addEventListener('open-customization-modal', this.openModal);
  },
  beforeDestroy() {
    console.log('test vue beforeDestroy rest', this);
    window.removeEventListener('resize', this.updateVw);
    window.removeEventListener('open-customization-modal', this.openModal);
  },
  methods: {
    updateVw() {
      this.vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    },
    openModal() {
      window.document.querySelector('body').classList.add('no-scroll');
      this.isVisible = true;

      if (this.section.theme_settings.productId) {
        if (this.section.theme_settings.productId === this.section.theme_settings.sample_product) {
          this.selectedKit = 'sample';
        } else if (this.section.theme_settings.productId === this.section.theme_settings.discovery_product) {
          this.selectedKit = 'discovery';
        }
      }
      this.getData();
    },
    closeModal() {
      window.document.querySelector('body').classList.remove('no-scroll');
      this.isVisible = false;
    },
    getKitLimitForCart() {
      switch (this.selectedKit) {
        case 'sample':
          return this.section.theme_settings.sample_limit;
        case 'discovery':
          return this.section.theme_settings.discovery_limit;
      }
      return 0;
    },
    getKitItemsLimit() {
      switch (this.selectedKit) {
        case 'sample':
          return this.section.theme_settings.limit_item_sample_kit;
        case 'discovery':
          return this.section.theme_settings.limit_item_discovery_set;
      }
      return 0;
    },
    canAddToCart() {
      // Required from xerjoff , can be improved
      if (this.selectedKit != 'sample') {
        return true;
      }

      const kitLimit =
        this.selectedKit === 'sample'
          ? this.section.theme_settings.sample_limit
          : this.section.theme_settings.discovery_limit;
      const kitHandle =
        this.selectedKit === 'sample'
          ? this.section.theme_settings.sample_handle
          : this.section.theme_settings.discovery_handle;
      let kitCount = 0;

      this.section.cartItems.forEach((item) => {
        if (item.handle === kitHandle) {
          kitCount += item.quantity;
        }
      });

      return kitCount < kitLimit;
    },
    getSampleVariant() {
      switch (this.selectedKit) {
        case 'sample':
          return 'gid://shopify/ProductVariant/' + this.section.theme_settings.sample_variant;
        case 'discovery':
          return 'gid://shopify/ProductVariant/' + this.section.theme_settings.discovery_variant;
      }
      return '';
    },
    canShowPrice() {
      return this.selectedKit != 'sample';
    },
    canAddProduct() {
      switch (this.selectedKit) {
        case 'sample':
          return this.selectedProducts.length < 4;
        case 'discovery':
          return this.selectedProducts.length < 3;
      }
      return true;
    },
    onUpdateIsOpen(menuItem) {
      this.selectedMenuItem = this.selectedMenuItem && this.selectedMenuItem.id === menuItem.id ? null : menuItem;
      if (this.selectedMenuItem) {
        this._mobileSectionOpen = 'filters';
      }
    },
    onCloseDetail() {
      this._isProductDetailOpen = false;
      setTimeout(() => {
        this.selectedProduct = undefined;
      }, 300);
    },
    onSearchKeyUp(event) {
      if (this.searchDebounceInput && event.key === 'Enter') {
        this._mobileSectionOpen = 'content';
        return;
      }
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.searchDebounceInput = event.target.value;
        this.onSearchProducts();
      }, 300);
    },
    onSearchSelect() {
      this._mobileSectionOpen = null;
    },
    onOpenSearch() {
      this._mobileSectionOpen = null;
      document.getElementById('mobile-search').focus();
    },
    onResetSearch() {
      this.onCloseDetail();
      this._searchInput = '';
      this.searchDebounceInput = '';
      this.searchProducts = [];
    },
    onSelectKit(kit) {
      this.selectedKit = kit;
      this.selectedProducts = [];
      this.getData();
    },
    async onSelectCategory(category) {
      this._searchInput = '';
      this.searchDebounceInput = '';
      this._mobileSectionOpen = 'content';
      if (this.selectedCategory && this.selectedCategory.resourceId === category.resourceId) {
        return;
      }
      this.selectedCategory = category;
      this.selectedProduct = undefined;
      this.onCloseDetail();
      const query = `query getCategoryById($id: ID!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language){
        collection(id: $id) {
          products(first: 250) {
            nodes {
              ${this.productQueryParams}
            }
          }
        }
      }`;

      try {
        const data = await this.query({
          query: query,
          variables: {
            country: this.section.theme_settings.country,
            language: this.section.theme_settings.language,
            id: category.resourceId,
          },
        });
        this.categoryProducts = data.collection.products.nodes.filter(
          (p) => p.selectedOrFirstAvailableVariant.availableForSale,
        );
      } catch (error) {
        console.error('Error fetching data', error);
      }
    },
    onProductDetail(product) {
      const detailProduct = product.metafields.find((m) => m && m.key && m.key === 'detail_link');
      if (detailProduct) {
        product = detailProduct.reference;
      }
      this.selectedProduct = product;
      this._isProductDetailOpen = true;
      this._mobileSectionOpen = 'content';
    },
    onProductAdd(product) {
      if (!this.canAddProduct()) {
        return;
      }
      this.selectedProducts.push(product);
    },
    onProductAddFromDetail(product) {
      for (const filteredProduct of this.filteredProducts) {
        const detailProduct = filteredProduct.metafields.find((m) => m && m.key && m.key === 'detail_link');
        if (detailProduct && detailProduct.reference.id === product.id) {
          this.onProductAdd(filteredProduct);
          return;
        }
      }
    },
    onProductRemove(product) {
      const index = this.selectedProducts.findIndex((p) => p.id === product.id);
      this.selectedProducts.splice(index, 1);
    },
    async onAddToCart() {
      if (!this.canAddToCart()) {
        return;
      }

      const bundleId = new Date().getTime();
      let formData = {
        items: this.selectedProducts.map((p) => ({
          id: p.selectedOrFirstAvailableVariant.id.split('/').pop(),
          quantity: 1,
          properties: {
            _bundleId: bundleId,
            _selectedKit: this.getSampleVariant(),
            _kitLimit: this.getKitItemsLimit(),
          },
        })),
      };

      const originUrl = window.location.origin + (this.section.pathPrefix === '/' ? '' : this.section.pathPrefix);

      await fetch(`${originUrl}/cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })
        .then((response) => {
          return response.json();
        })
        .catch((error) => {
          console.error('Error:', error);
        });

      window.location.href = `${originUrl}/cart`;
    },
    async onSearchProducts() {
      if (!this.searchDebounceInput) {
        this.searchProducts = [];
        return;
      }

      this.onCloseDetail();
      const query = `query searchProducts($search: String, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language){
        products(first: 250, query: $search) {
          nodes {
            ${this.productQueryParams}
            collections(first: 10) {
              nodes {
                id
              }
            }
          }
        }
      }`;

      try {
        const data = await this.query({
          query: query,
          variables: {
            country: this.section.theme_settings.country,
            language: this.section.theme_settings.language,
            search: `title:*${this.searchDebounceInput}*`,
          },
        });
        const collections = this.menu.items.map((i) => i.items.map((i) => i.resourceId)).flat();
        this.searchProducts = data.products.nodes.filter(
          (p) =>
            p.selectedOrFirstAvailableVariant.availableForSale &&
            p.collections.nodes.some((c) => collections.includes(c.id)),
        );
      } catch (error) {
        console.error('Error fetching data', error);
      }
    },

    async getData() {
      if (!this.selectedKit) {
        return;
      }

      const query = `query getInitialData($handle: String!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language){
        menu(handle: $handle) {
          title
          items {
            id
            title
            items {
              title
              resourceId
            }
          }
        }
      }`;

      try {
        const data = await this.query({
          query: query,
          variables: {
            country: this.section.theme_settings.country,
            language: this.section.theme_settings.language,
            handle: this.section.settings[`${this.selectedKit}_menu`],
          },
        });
        this.menu = data.menu;
        for (let item of this.menu.items) {
          item.items = item.items.filter((i) => i.resourceId);
        }
        this.menu.items = this.menu.items.filter((m) => m.items.length > 0);

        if (this.vw > 750) {
          this.selectedMenuItem = this.menu.items[0];
        }
      } catch (error) {
        console.error('Error fetching data', error);
      }
    },

    async query(query, signal = null) {
      this._loading = true;
      return fetch('/api/2025-01/graphql.json', {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this.section.theme_settings.storefront_access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(query),
        signal,
      })
        .then((response) => response.json())
        .then((data) => data.data)
        .finally(() => (this._loading = false));
    },
  },
  watch: {
    vw(newVal, oldVal) {
      if ((newVal >= 1008 && oldVal < 1008) || (newVal < 1008 && oldVal >= 1008)) {
        this.getData();
      }
    },
  },
};
</script>
